import { action, computed, makeObservable } from 'mobx';

import {
  CsatSettingsServer,
  ICsatSettings
} from 'shared/entities/projectSettings';
import { FieldModel } from 'shared/models/form';
import {
  validateIsEmpty,
  validateTextFieldMaxLength
} from 'shared/entities/validator';

/** Лимит prompt_text из OpenAPI CsatSettings. */
export const CSAT_PROMPT_MAX_LENGTH = 500;

export default class CsatSettingsModel {
  readonly enabled: FieldModel<boolean>;
  readonly promptText: FieldModel<string>;
  readonly askComment: FieldModel<boolean>;

  constructor(data: ICsatSettings) {
    this.enabled = new FieldModel(data.enabled);
    this.askComment = new FieldModel(data.askComment);
    this.promptText = new FieldModel(data.promptText, [
      validateIsEmpty,
      (value) => validateTextFieldMaxLength(value, CSAT_PROMPT_MAX_LENGTH)
    ]);

    makeObservable(this, {
      touched: computed,
      isValid: computed,
      canSave: computed,
      changeEnabled: action.bound,
      changePromptText: action.bound,
      resetPrompt: action.bound,
      resetTouchedValues: action.bound,
      resetToPayload: action.bound,
      reset: action.bound
    });
  }

  get touched(): boolean {
    return (
      this.enabled.touched || this.promptText.touched || this.askComment.touched
    );
  }

  get isValid(): boolean {
    return !this.enabled.value || !this.promptText.isError;
  }

  get canSave(): boolean {
    return this.touched && this.isValid;
  }

  changeEnabled(value: boolean): void {
    this.enabled.changeValue(value);

    // Бэкенд валидирует prompt_text независимо от enabled, поэтому при
    // выключении откатываем текст к последнему сохранённому значению.
    if (!value) {
      this.promptText.reset();
      return;
    }

    // Пустое исходное значение (CSAT ещё не настраивали) — сразу ставим
    // ошибку, чтобы canSave вернул false.
    this.promptText.validate();
  }

  changePromptText(value: string): void {
    this.promptText.changeValue(value);

    if (this.enabled.value) {
      this.promptText.validate();
    }
  }

  /** Откатить текст prompt к исходному сохранённому значению. */
  resetPrompt(): void {
    this.promptText.reset();

    // reset() очищает ошибку; если сохранённое значение пустое, вернём ошибку,
    // чтобы canSave вернул false.
    if (this.enabled.value) {
      this.promptText.validate();
    }
  }

  /** Зафиксировать текущие значения как последний сохранённый снимок. */
  resetTouchedValues(): void {
    this.enabled.resetTouched();
    this.promptText.resetTouched();
    this.askComment.resetTouched();
  }

  /** Вернуть подтверждённый сервером снимок после ошибки сохранения. */
  resetToPayload(payload: CsatSettingsServer): void {
    this.enabled.changeValue(payload.enabled);
    this.promptText.changeValue(payload.prompt_text);
    this.askComment.changeValue(payload.ask_comment);
    this.resetTouchedValues();
  }

  /** Вернуть поля к последнему сохранённому состоянию после ошибки. */
  reset(): void {
    this.enabled.reset();
    this.promptText.reset();
    this.askComment.reset();
  }

  toJSON(): CsatSettingsServer {
    return {
      enabled: this.enabled.value,
      prompt_text: this.promptText.value,
      ask_comment: this.askComment.value
    };
  }
}
